import { Inject, Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, combineLatest, distinctUntilChanged, from, map, of, shareReplay, switchMap } from 'rxjs';
import { PermissionDeliveryOption } from '../sharedModules/permission/keys/permissionDelivery.interface';
import { PermissionService } from '../sharedModules/permission/permission.service';
import { UserDataService } from './user-data.service';
import { MU_CHAT_CONFIG, MuChatConfig } from './mu-chat.config';
import { buildArenaWidgetUrl } from './mu-chat-url';

@Injectable({
  providedIn: 'root',
})
export class MuChatService {
  private readonly token$: Observable<string> = this.userDataService.getTokenLocal().pipe(
    map(token => token ?? ''),
    distinctUntilChanged()
  );

  /** Only depends on the user id, so a token change never re-queries the permission. */
  private readonly restricted$: Observable<boolean> = this.userDataService.getUserData().pipe(
    map(userData => userData?.id || ''),
    distinctUntilChanged(),
    switchMap(userId => (userId ? from(this.permissionService.canAccess(PermissionDeliveryOption.DELIVERY_ORDER_REMOVE_CHAT)) : of(false))),
    distinctUntilChanged()
  );

  /** Sanitized ARENA widget URL, or null when the chat must not render. Never logged or stored. */
  readonly iframeUrl$: Observable<SafeResourceUrl | null> = combineLatest([this.token$, this.restricted$]).pipe(
    map(([token, restricted]) => (restricted ? null : buildArenaWidgetUrl(this.config.widgetBaseUrl, this.config.slug, token))),
    distinctUntilChanged(),
    map(url => (url === null ? null : this.sanitizer.bypassSecurityTrustResourceUrl(url))),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private permissionService: PermissionService,
    private userDataService: UserDataService,
    private sanitizer: DomSanitizer,
    @Inject(MU_CHAT_CONFIG) private config: MuChatConfig
  ) {}
}
